/* ============================================================
   shop-rail.js — self-hosted Shopify product promo widget
   No app store, no subscription, no dependencies, no build step.

   The Diver Guy layout:

   WIDE DESKTOP (viewport >= breakpoint, default 1150px):
     - LEFT panel  — first product from the `leftCollection`,
                     square image + CTA, fixed to the left gutter,
                     vertically centered. Appears on load.
     - RIGHT panel — first product from the `rightCollection`,
                     larger image, heading "Featured item",
                     fixed bottom-right. Appears on load.

   NARROW / MOBILE (viewport < breakpoint):
     - Side panels are hidden (they'd overlap the text).
     - Two inline ads are placed inside the article instead:
         * ad #1 (left product)  after paragraph 3
         * ad #2 (right product) after paragraph 6, or at the
           very end if there is no 6th paragraph.

   Everything renders in Shadow DOM so page CSS and widget CSS
   can never collide.

   SETUP: edit CONFIG, upload as a theme asset, add one <script>
   tag before </body>. See README.md.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- CONFIG — edit this block only ---------- */
  const CONFIG = {
    // Storefront domain — primary customer-facing domain, no https://,
    // no trailing slash, KEEP the www. (non-www redirects break CORS).
    shop: 'www.thediverguy.com',

    // ---- PAGE TARGETING ----
    // Each slot shows a COLLECTION (its image + "starting at" lowest price).
    // Which collections depends on the page: the widget checks the current URL
    // against `rules` top to bottom and uses the FIRST match; if none match,
    // `defaultRule`.
    //
    // `match` is a keyword looked for in the page's URL path. Keep it unique
    // to the page(s) you mean. An empty `right` means that panel is hidden on
    // those pages (left-only). To add a page later, add one line — no code.
    // Optional per-rule overrides: leftImage/leftTitle, rightImage/rightTitle.
    //
    // linkTo: where a click goes.
    //   'product'    -> straight to the promoted product (the first product in
    //                   the collection). Best for single-product "test" slots.
    //   'collection' -> the collection page. Best when the collection has many
    //                   items to browse (e.g. magnets/stickers).
    rules: [
      { match: 'springboard-diving-parents-guide', left: 'diver-guy-magnets', right: '', linkTo: '' },
    ],

    // Used on every page that matches no rule above. Default collections hold
    // one product each (swap it to A/B test), so link straight to that product.
    defaultRule: { left: 'left-feature', right: 'blog-picks', linkTo: 'product' },

    // ---- WHERE THE WIDGET RUNS ----
    // exclude: URL keywords where the widget does NOT load at all. Add pages
    // here to keep the widget off them (e.g. pages running Google ads instead).
    exclude: ['contact', 'about-the-diver-guy', 'policies', 'sitemap'
      // 'checkout', 'some-landing-page',
    ],
    // includeOnly: if this list is non-empty, the widget loads ONLY on pages
    // whose URL contains one of these keywords, and nowhere else. Leave empty
    // to run everywhere except the exclude list above.
    includeOnly: [
      // 'blogs/news', 'springboard-diving-parents-guide',
    ],

    // Below this viewport width, side panels hide and the two inline
    // ads take over inside the article.
    breakpoint: 1150,

    // Look
    accent: '#0b6e8f',
    radius: '12px',
    showPrice: true,
    pricePrefix: 'Starting at',  // text before the lowest collection price
    leftCta: 'Shop now',
    rightHeading: 'Featured item',
    rightCta: 'View',
    // Panels always link to the collection page (with UTM tracking).

    // Dismissal: side panels get an X. Once closed, they stay closed on
    // future pages/visits for this many days (0 = forever until the visitor
    // clears their browser). Inline ads are part of the article and aren't
    // dismissible.
    dismissDays: 2,

    // Shown when a product has no image (full https:// URL, e.g. your
    // logo). '' = gradient placeholder.
    fallbackImage: '',

    // Article body selector, and which paragraphs the inline ads follow.
    inlineContainer: '.rte',
    inlineFirstAfter: 3,
    inlineSecondAfter: 6,

    // Attribution — appears in Shopify referrer reports.
    utm: { source: 'tdg-blog', medium: 'widget', campaign: 'shop-rail' },

    // Browser-side cache so repeat pageviews don't refetch. Minutes.
    cacheMinutes: 30,
  };
  /* ---------- end of config ---------- */

  // Pick the collections for this page: first rule whose `match` keyword is
  // in the URL path wins; otherwise the default. A page-level override marker
  // (<div data-shop-left="handle" data-shop-right="handle">) beats the rules.
  // Returns handles plus optional per-slot image/title overrides from the rule.
  function resolveCollections() {
    const path = (location.pathname || '').toLowerCase();

    const marker = document.querySelector('[data-shop-left]');
    if (marker) {
      const mLink = marker.getAttribute('data-shop-link') || 'product';
      return {
        left: marker.getAttribute('data-shop-left') || '',
        right: marker.getAttribute('data-shop-right') || '',
        leftOverride: { linkTo: mLink }, rightOverride: { linkTo: mLink },
      };
    }

    const rules = CONFIG.rules || [];
    let r = CONFIG.defaultRule;
    for (let i = 0; i < rules.length; i++) {
      const cand = rules[i];
      if (cand && cand.match && path.indexOf(String(cand.match).toLowerCase()) !== -1) {
        r = cand; break;
      }
    }
    // linkTo: 'product' (default — go straight to the item) or 'collection'.
    const linkTo = r.linkTo === 'collection' ? 'collection' : 'product';
    return {
      left: r.left || '',
      right: r.right || '',
      // Optional per-rule overrides: leftImage/leftTitle, rightImage/rightTitle.
      leftOverride: { image: r.leftImage || '', title: r.leftTitle || '', linkTo: linkTo },
      rightOverride: { image: r.rightImage || '', title: r.rightTitle || '', linkTo: linkTo },
    };
  }

  const money = function (v) {
    return '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  // "Starting at $X" — or empty string if no price could be determined.
  const priceLabel = function (c) {
    if (c.priceFrom === null || c.priceFrom === undefined) return '';
    return CONFIG.pricePrefix + ' ' + money(c.priceFrom);
  };

  const collectionURL = function (handle) {
    const u = new URL('https://' + CONFIG.shop + '/collections/' + handle);
    u.searchParams.set('utm_source', CONFIG.utm.source);
    u.searchParams.set('utm_medium', CONFIG.utm.medium);
    u.searchParams.set('utm_campaign', CONFIG.utm.campaign);
    return u.toString();
  };

  const productURL = function (handle) {
    const u = new URL('https://' + CONFIG.shop + '/products/' + handle);
    u.searchParams.set('utm_source', CONFIG.utm.source);
    u.searchParams.set('utm_medium', CONFIG.utm.medium);
    u.searchParams.set('utm_campaign', CONFIG.utm.campaign);
    return u.toString();
  };

  // Where a panel links: to the promoted product (default) or, when the rule
  // sets linkTo:'collection', to the collection page. Falls back to the
  // collection page if a product handle isn't available.
  const hrefFor = function (c) {
    if (c.linkTo === 'collection' || !c.productHandle) {
      return collectionURL(c.handle);
    }
    return productURL(c.productHandle);
  };

  /* ---------- data ---------- */

  // Lowest price across every variant of every product in a collection.
  function lowestPrice(products) {
    let min = null;
    (products || []).forEach(function (p) {
      (p.variants || []).forEach(function (v) {
        const price = parseFloat(v.price);
        if (!isNaN(price) && (min === null || price < min)) min = price;
      });
    });
    return min;
  }

  // First available product image in the collection, as a fallback when the
  // collection itself has no image set in Shopify.
  function firstProductImage(products) {
    for (let i = 0; i < (products || []).length; i++) {
      const imgs = products[i].images;
      if (imgs && imgs[0] && imgs[0].src) return imgs[0].src;
    }
    return '';
  }

  const cacheKey = function (coll) { return 'shoprail:c:' + CONFIG.shop + ':' + coll; };

  function readCache(coll) {
    try {
      const raw = sessionStorage.getItem(cacheKey(coll));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.at > CONFIG.cacheMinutes * 60000) return null;
      return parsed.data;
    } catch (e) { return null; }
  }

  function writeCache(coll, data) {
    try {
      sessionStorage.setItem(cacheKey(coll), JSON.stringify({ at: Date.now(), data: data }));
    } catch (e) {}
  }

  const promises = {};

  /* ---------- collection index (title + image), fetched once ---------- */
  // Shopify's product feed doesn't include a collection's own image/title, so
  // we read the store-wide /collections.json list once and look up by handle.

  let indexPromise = null;
  function collectionsIndex() {
    if (indexPromise) return indexPromise;
    const url = 'https://' + CONFIG.shop + '/collections.json?limit=250';
    indexPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : { collections: [] }; })
      .then(function (data) {
        const map = {};
        (data.collections || []).forEach(function (c) {
          map[c.handle] = {
            title: c.title || '',
            image: (c.image && c.image.src) || '',
          };
        });
        return map;
      })
      .catch(function () { return {}; });
    return indexPromise;
  }

  /* ---------- dismissal persistence (localStorage, cross-page) ---------- */

  const DISMISS_KEY = 'shoprail:dismissed';

  function isDismissed() {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      const at = Number(raw);
      if (!at) return false;
      if (CONFIG.dismissDays === 0) return true; // forever
      return Date.now() - at < CONFIG.dismissDays * 86400000;
    } catch (e) { return false; }
  }

  function markDismissed() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
  }

  // Returns { handle, title, image, priceFrom } for a collection, or null.
  // `override` is the matched rule, which may carry image/title overrides
  // and a linkTo preference ('product' | 'collection').
  function collectionData(coll, override) {
    const ov = override || {};
    const linkTo = ov.linkTo === 'collection' ? 'collection' : 'product';
    const key = coll + '|' + (ov.image || '') + '|' + (ov.title || '') + '|' + linkTo;
    if (promises[key]) return promises[key];

    const cached = readCache(key);
    if (cached) {
      promises[key] = Promise.resolve(cached);
      return promises[key];
    }

    const prodUrl = 'https://' + CONFIG.shop + '/collections/' + coll + '/products.json?limit=250';

    promises[key] = Promise.all([
      fetch(prodUrl).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + coll);
        return r.json();
      }),
      collectionsIndex(),
    ])
      .then(function (res) {
        const products = res[0].products || [];
        const meta = res[1][coll] || {};
        if (!products.length && !meta.title) return null;

        const first = products[0];
        const data = {
          handle: coll,
          title: ov.title || meta.title || coll,
          image: ov.image || meta.image || firstProductImage(products) || CONFIG.fallbackImage || '',
          priceFrom: lowestPrice(products),
          productHandle: first ? first.handle : '',
          linkTo: linkTo,
        };
        writeCache(key, data);
        return data;
      })
      .catch(function (err) {
        console.warn('[shop-rail] ' + coll + ': ' + err.message);
        return null;
      });

    return promises[key];
  }

  /* ---------- styles ---------- */

  const CSS = [
    ':host { all: initial; display: block !important; }',
    '* { box-sizing: border-box; }',
    '.wrap { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #16181d; line-height: 1.4; }',
    '.heading { font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; color: #6b7280; margin: 0 0 10px; }',
    '.buy { display: inline-block; padding: 7px 16px; font-size: 13px; font-weight: 600; border-radius: 999px; background: var(--accent); color: #fff; text-decoration: none; white-space: nowrap; cursor: pointer; }',
    '.buy:focus-visible { outline: 2px solid #16181d; outline-offset: 2px; }',
    '.thumb-fallback { display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #a7c7d6, #0b6e8f); color: #fff; font-size: 11px; letter-spacing: .04em; }',

    /* dismiss button — shared by both side panels */
    '.close { position: absolute; top: 6px; right: 6px; width: 26px; height: 26px; border: 0; border-radius: 50%; background: rgba(255,255,255,.92); color: #6b7280; font-size: 16px; line-height: 24px; text-align: center; cursor: pointer; padding: 0; box-shadow: 0 1px 4px rgba(0,0,0,.18); z-index: 2; }',
    '.close:hover { background: #fff; color: #16181d; }',
    '.close:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }',

    '.left { position: relative; width: 220px; background: #fff; border: 1px solid #e5e7eb; border-radius: var(--radius); box-shadow: 0 8px 24px rgba(0,0,0,.12); overflow: hidden; text-align: center; }',
    '.left a.imglink { display: block; text-decoration: none; }',
    '.left .thumb { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; display: block; }',
    '.left .body { padding: 10px 12px 12px; }',
    '.left .title { font-size: 12px; font-weight: 500; line-height: 1.3; margin: 0 0 3px; color: #4b5563; }',
    '.left .price { font-size: 13px; font-weight: 700; color: var(--accent); display: block; margin-bottom: 9px; }',

    '.right { position: relative; width: 230px; background: #fff; border: 1px solid #e5e7eb; border-radius: var(--radius); box-shadow: 0 8px 28px rgba(0,0,0,.14); padding: 14px; }',
    '.right a.imglink { display: block; text-decoration: none; }',
    '.right .thumb { width: 100%; height: 140px; border-radius: 8px; object-fit: cover; margin-bottom: 10px; }',
    '.right .row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }',
    '.right .title { font-size: 14px; font-weight: 600; line-height: 1.3; margin: 0 0 3px; color: #16181d; }',
    '.right .price { font-size: 13px; font-weight: 700; color: var(--accent); }',

    '.inline { display: flex; gap: 12px; align-items: center; border: 1px solid #e5e7eb; border-radius: var(--radius); background: #fafafa; padding: 12px; margin: 22px 0; text-decoration: none; color: inherit; }',
    '.inline:hover { border-color: var(--accent); }',
    '.inline .thumb { width: 60px; height: 60px; flex: 0 0 60px; border-radius: 8px; object-fit: cover; }',
    '.inline .meta { flex: 1; min-width: 0; }',
    '.inline .title { font-size: 14px; font-weight: 600; margin: 0 0 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }',
    '.inline .price { font-size: 13px; font-weight: 700; color: var(--accent); }',

    '@media (prefers-reduced-motion: reduce) { * { transition: none !important; } }',
  ].join('\n');

  function thumbFor(p, cls, label) {
    if (p.image) {
      return '<img class="' + cls + '" src="' + p.image + '" alt="" loading="lazy">';
    }
    return '<div class="' + cls + ' thumb-fallback">' + (label || 'product') + '</div>';
  }

  /* ---------- shadow host helpers (sizing fix baked in) ---------- */

  function makeHost() {
    const host = document.createElement('div');
    host.style.setProperty('display', 'block', 'important');
    host.style.setProperty('opacity', '1', 'important');
    host.style.setProperty('transform', 'none', 'important');
    host.style.setProperty('visibility', 'visible', 'important');
    return host;
  }

  function attach(host) {
    const root = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent =
      ':host{--accent:' + CONFIG.accent + ';--radius:' + CONFIG.radius + ';}' + CSS;
    root.appendChild(style);
    return root;
  }

  /* ---------- desktop panels ---------- */

  // Clicking X on either side panel dismisses BOTH (one experience) and
  // remembers the choice across pages via localStorage.
  function wireDismiss(root, host) {
    const btn = root.querySelector('.close');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      markDismissed();
      teardown(state.desktopHosts);
    });
  }

  function renderLeft(coll) {
    const host = makeHost();
    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('left', '18px', 'important');
    host.style.setProperty('top', '50%', 'important');
    host.style.setProperty('transform', 'translateY(-50%)', 'important');
    host.style.setProperty('width', '200px', 'important');
    host.style.setProperty('z-index', '2147483000', 'important');
    document.body.appendChild(host);

    const root = attach(host);
    const box = document.createElement('div');
    box.className = 'wrap left';
    box.innerHTML =
      '<button class="close" aria-label="Dismiss">&times;</button>' +
      '<a class="imglink" href="' + hrefFor(coll) + '" target="_blank" rel="noopener">' +
        thumbFor(coll, 'thumb', 'collection image') +
      '</a>' +
      '<div class="body">' +
        '<p class="title">' + coll.title + '</p>' +
        (CONFIG.showPrice && priceLabel(coll) ? '<span class="price">' + priceLabel(coll) + '</span>' : '') +
        '<a class="buy" href="' + hrefFor(coll) + '" target="_blank" rel="noopener">' +
          CONFIG.leftCta + '</a>' +
      '</div>';
    root.appendChild(box);
    wireDismiss(root, host);
    return host;
  }

  function renderRight(coll) {
    const host = makeHost();
    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('right', '18px', 'important');
    host.style.setProperty('bottom', '18px', 'important');
    host.style.setProperty('width', '230px', 'important');
    host.style.setProperty('z-index', '2147483000', 'important');
    document.body.appendChild(host);

    const root = attach(host);
    const box = document.createElement('div');
    box.className = 'wrap right';
    box.innerHTML =
      '<button class="close" aria-label="Dismiss">&times;</button>' +
      '<p class="heading">' + CONFIG.rightHeading + '</p>' +
      '<a class="imglink" href="' + hrefFor(coll) + '" target="_blank" rel="noopener">' +
        thumbFor(coll, 'thumb', 'featured') +
      '</a>' +
      '<div class="row"><div>' +
        '<p class="title">' + coll.title + '</p>' +
        (CONFIG.showPrice && priceLabel(coll) ? '<span class="price">' + priceLabel(coll) + '</span>' : '') +
      '</div>' +
      '<a class="buy" href="' + hrefFor(coll) + '" target="_blank" rel="noopener">' +
        CONFIG.rightCta + '</a></div>';
    root.appendChild(box);
    wireDismiss(root, host);
    return host;
  }

  /* ---------- inline ads ---------- */

  function makeInline(coll) {
    const host = makeHost();
    const root = attach(host);
    const a = document.createElement('a');
    a.className = 'wrap inline';
    a.href = hrefFor(coll);
    a.target = '_blank';
    a.rel = 'noopener';
    a.innerHTML =
      thumbFor(coll, 'thumb', 'img') +
      '<div class="meta">' +
        '<p class="title">' + coll.title + '</p>' +
        (CONFIG.showPrice && priceLabel(coll) ? '<span class="price">' + priceLabel(coll) + '</span>' : '') +
      '</div>' +
      '<span class="buy">' + CONFIG.rightCta + '</span>';
    root.appendChild(a);
    return host;
  }

  function placeAfterParagraph(container, n, host) {
    const paras = container.querySelectorAll('p');
    const anchor = paras[n - 1];
    if (anchor) anchor.after(host);
    else container.appendChild(host);
  }

  /* ---------- boot ---------- */

  const state = { desktopHosts: [], inlineHosts: [], built: null };

  function buildDesktop(left, right) {
    if (state.desktopHosts.length) return;
    if (isDismissed()) return;
    if (left) state.desktopHosts.push(renderLeft(left));
    if (right) state.desktopHosts.push(renderRight(right));
  }

  function buildInline(left, right) {
    if (state.inlineHosts.length) return;
    const container = document.querySelector(CONFIG.inlineContainer);
    if (!container) return;
    if (left) {
      const h1 = makeInline(left);
      placeAfterParagraph(container, CONFIG.inlineFirstAfter, h1);
      state.inlineHosts.push(h1);
    }
    if (right) {
      const h2 = makeInline(right);
      placeAfterParagraph(container, CONFIG.inlineSecondAfter, h2);
      state.inlineHosts.push(h2);
    }
  }

  function teardown(list) {
    list.forEach(function (h) { h.remove(); });
    list.length = 0;
  }

  function sync(left, right) {
    const desktop = window.innerWidth >= CONFIG.breakpoint;
    if (state.built === desktop) return;
    state.built = desktop;

    if (desktop) {
      teardown(state.inlineHosts);
      buildDesktop(left, right);
    } else {
      teardown(state.desktopHosts);
      buildInline(left, right);
    }
  }

  // Should the widget run on this URL at all? Honors includeOnly (allowlist)
  // and exclude (blocklist). includeOnly wins when non-empty.
  function widgetAllowedHere() {
    const path = (location.pathname || '').toLowerCase();
    const has = function (list) {
      return (list || []).some(function (kw) {
        return kw && path.indexOf(String(kw).toLowerCase()) !== -1;
      });
    };
    if ((CONFIG.includeOnly || []).length) return has(CONFIG.includeOnly);
    return !has(CONFIG.exclude);
  }

  function init() {
    if (!widgetAllowedHere()) return;

    const picked = resolveCollections();

    // A blank handle means that slot is intentionally off on this page.
    const leftReq = picked.left ? collectionData(picked.left, picked.leftOverride) : Promise.resolve(null);
    const rightReq = picked.right ? collectionData(picked.right, picked.rightOverride) : Promise.resolve(null);

    Promise.all([leftReq, rightReq]).then(function (res) {
      const left = res[0], right = res[1];
      if (!left && !right) return;

      sync(left, right);

      let t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(function () { sync(left, right); }, 150);
      }, { passive: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();